/**
 * api/client.ts
 * ─────────────
 * Thin fetch wrapper that:
 *  - Reads the stored JWT from sessionStorage and injects it as a Bearer header.
 *  - Normalises error responses into a typed ApiError.
 *  - Supports JSON, multipart/form-data, and file-download responses.
 */

const API_BASE =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || '';

// ── Storage key ───────────────────────────────────────────────────────────────

const TOKEN_KEY = 'infinium_access_token';

export function saveToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function loadToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

// ── Error type ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Error message extraction ──────────────────────────────────────────────────
// Handles FastAPI's various error shapes:
//   { detail: "string" }
//   { detail: [{ msg: "...", loc: [...] }] }            ← pydantic validation
//   { detail: { detail: "...", missing_columns: [...] } } ← ingestion errors

function extractMessage(raw: unknown, status: number): string {
  if (raw == null) return `HTTP ${status}`;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw))
    return (raw as { msg?: string; message?: string }[])
      .map(e => e.msg ?? e.message ?? JSON.stringify(e))
      .join('; ');
  if (typeof raw === 'object') {
    // Nested ingestion error: { detail: "msg", missing_columns: [...] }
    const nested = (raw as Record<string, unknown>);
    if (typeof nested.detail === 'string') return nested.detail;
    if (typeof nested.message === 'string') return nested.message;
    // Fallback: first string value we find
    for (const v of Object.values(nested))
      if (typeof v === 'string') return v;
    return JSON.stringify(raw);
  }
  return String(raw);
}

// ── Core request helper ───────────────────────────────────────────────────────

async function request<T>(
  path: string,
  init: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  const { skipAuth = false, ...fetchInit } = init;

  const headers = new Headers(fetchInit.headers);

  if (!skipAuth) {
    const token = loadToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Only set Content-Type for JSON bodies; let the browser set multipart boundary
  if (
    fetchInit.body &&
    !(fetchInit.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...fetchInit,
    headers,
  });

  if (!response.ok) {
    let detail: unknown;
    try {
      detail = await response.json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        // Body stream already consumed (e.g. truncated proxy response).
        detail = `HTTP ${response.status}`;
      }
    }
    const raw = typeof detail === 'object' && detail !== null && 'detail' in detail
      ? (detail as Record<string, unknown>).detail
      : null;
    const message = extractMessage(raw, response.status);
    throw new ApiError(response.status, message, detail);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ── Exported API methods ──────────────────────────────────────────────────────

export const apiClient = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'GET' });
  },

  post<T>(path: string, body: unknown, options?: { skipAuth?: boolean }): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    });
  },

  postForm<T>(path: string, form: FormData): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: form,
    });
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  delete<T = void>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },

  /** Return the raw Response so caller can stream or read as blob. */
  getRaw(path: string): Promise<Response> {
    const token = loadToken();
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, { method: 'GET', headers });
  },
};
